import { useCallback, useEffect, useRef } from 'react';

import { SortConfig } from './list-filter-types';

interface PersistedFilterData<TFilters, TExtras = Record<string, unknown>> {
  filters: TFilters;
  sort?: SortConfig;
  extras?: TExtras;
}

type StoredData<TFilters, TExtras = Record<string, unknown>> = Record<string, PersistedFilterData<TFilters, TExtras>>;

/**
 * Synchronously reads persisted filter data for a given org from localStorage.
 * Used as a useState lazy initialiser so the first render already has persisted values (no flicker).
 */
export const readPersistedFilters = (storageKey: string, organisationId: string): PersistedFilterData<Record<string, unknown>> | undefined => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return undefined;
    }
    const stored: StoredData<Record<string, unknown>> = JSON.parse(raw);
    const orgData = stored[organisationId];
    return orgData?.filters ? orgData : undefined;
  } catch {
    return undefined;
  }
};

interface UseListFilterPersistenceArgs<TFilters, TExtras = Record<string, unknown>> {
  /** localStorage key */
  storageKey: string;
  /** Organisation ID to scope stored filters */
  organisationId: string;
  /** Current filter state to persist */
  filters: TFilters;
  /** Current sort config to persist */
  sort: SortConfig | undefined;
  /** Optional extra values to persist alongside filters (e.g. view preferences) */
  extras?: TExtras;
  /** Callback to restore filters from storage */
  onRestore: (filters: TFilters, sort: SortConfig | undefined, extras: TExtras | undefined) => void;
  /**
   * Optional function to deserialize date strings back to Date objects
   * after JSON.parse (which turns Dates into strings).
   */
  deserializeDates?: (filters: TFilters) => TFilters;
}

/**
 * Generic hook for persisting list filter + sort state to localStorage.
 * Scoped by organisationId so each org has its own saved filters.
 *
 * On mount, restores saved filters if they exist.
 * On every filter/sort change, writes the current state to localStorage.
 */
export const useListFilterPersistence = <TFilters, TExtras = Record<string, unknown>>({
  storageKey,
  organisationId,
  filters,
  sort,
  extras,
  onRestore,
  deserializeDates
}: UseListFilterPersistenceArgs<TFilters, TExtras>) => {
  const isInitialized = useRef(false);
  // Prevents the save effect from overwriting localStorage with default filters
  // on the same render cycle where restore queued a setState (not yet processed).
  const pendingRestore = useRef(false);

  // Restore from localStorage on mount / org change
  useEffect(() => {
    isInitialized.current = false;
    pendingRestore.current = false;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        isInitialized.current = true;
        return;
      }

      const stored: StoredData<TFilters, TExtras> = JSON.parse(raw);
      const orgData = stored[organisationId];

      if (orgData && orgData.filters) {
        const restoredFilters = deserializeDates
          ? deserializeDates(orgData.filters)
          : orgData.filters;

        pendingRestore.current = true;
        onRestore(restoredFilters, orgData.sort, orgData.extras);
      }
    } catch {
      // If parsing fails, ignore stored data and use defaults
    }

    isInitialized.current = true;
    // Only run on mount and org change — onRestore and deserializeDates should be stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, organisationId]);

  // Save to localStorage on every filter/sort change (after initial restore)
  useEffect(() => {
    if (!isInitialized.current) {
      return;
    }

    // Skip the save that runs on the same cycle as restore — filters still hold
    // default values because React hasn't processed the onRestore setState yet.
    if (pendingRestore.current) {
      pendingRestore.current = false;
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      const stored: StoredData<TFilters, TExtras> = raw ? JSON.parse(raw) : {};

      stored[organisationId] = { filters, sort, extras };
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch {
      // Silently fail if localStorage is full or unavailable
    }
  }, [storageKey, organisationId, filters, sort, extras]);

  /** Clears stored filters for the current organisation */
  const clearPersistedFilters = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return;
      }

      const stored: StoredData<TFilters, TExtras> = JSON.parse(raw);
      delete stored[organisationId];
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch {
      // Silently fail
    }
  }, [storageKey, organisationId]);

  return { clearPersistedFilters };
};
